<template>
  <div
    ref="layoutRoot"
    class="macro-container"
  >

    <!-- Loading durante il caricamento delle macrocategorie -->
    <div
      v-if="loadingMacrocategorie"
      class="loading-screen"
    >
      <div class="loading-box">
        <b-spinner
          variant="primary"
          label="Caricamento"
        />
        <span>Caricamento dati...</span>
      </div>
    </div>

    <div class="macro-layout">

      <!-- LISTA -->
      <div class="macro-list-col">
        <b-card
          no-body
          class="h-100"
        >
          <div class="panel-header">
            <div class="macro-header-title">
              Macrocategorie
            </div>

            <b-form-select
              v-model="anno"
              :options="anni"
              class="macro-year"
              @change="caricaMacro"
            />

            <div class="macro-total">
              Totale speso:
              <strong>
                {{ formatEuro(totale) }}
              </strong>
            </div>
          </div>

          <div class="macro-list-body">
            <div
              v-for="categoria in categorie"
              :key="categoria.id_macro"
              class="macro-item"
            >

              <!-- NOME + IMPORTO -->
              <div class="macro-row">
                <div class="macro-name">
                  {{ categoria.nome }}
                </div>

                <div class="macro-value">
                  {{ formatEuro(categoria.valore) }}
                </div>
              </div>

              <!-- BARRA + PERCENTUALE -->
              <div class="macro-progress-row">
                <div class="macro-progress">
                  <div
                    class="macro-progress-bar"
                    :style="{
                      width: categoria.percentuale + '%',
                      backgroundColor: coloreCategoria(categoria.id_macro)
                    }"
                  />
                </div>

                <div class="macro-percentuale">
                  {{ categoria.percentuale }}%
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- GRAFICO -->
      <div class="macro-chart-col">
        <b-card
          no-body
          class="h-100"
        >
          <div class="panel-header chart-header">
            Distribuzione per categoria
          </div>

          <div class="macro-chart-body">
            <canvas
              ref="pieChart"
            />
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js";
import picService from "@/services/pic.service";

export default {

  name: "PICMacrocategorie",

  data() {
    return {
      anno: "all",
      anni: [],
      categorie: [],
      totale: 0,
      loadingMacrocategorie: false,
      chart: null,
      resizeObserver: null,
      resizeTimeout: null,

      /*
       * Palette utilizzata per le macrocategorie.
       * Le tonalità procedono dal blu più scuro
       * verso tonalità progressivamente più chiare.
       */
      coloriDisponibili: [
        "#17365D",
        "#1F4E79",
        "#2E75B6",
        "#3D85C6",
        "#4A90C2",
        "#5B9BD5",
        "#6FA8DC",
        "#8DB9E2",
        "#A9CBE8",
        "#C6DDF0"
      ],

      mappaColori: {}
    };
  },

  computed: {

    categorieGrafico() {

      const ordinate = [...this.categorie]
        .sort((a, b) => b.valore - a.valore);

      /*
       * Nel grafico vengono visualizzate al massimo 10 sezioni:
       * le prime 9 macrocategorie per importo e una sezione "Altre"
       * contenente la somma delle categorie rimanenti.
       */
      const limite = 9;

      if (ordinate.length <= limite) {
        return ordinate;
      }

      const principali = ordinate.slice(0, limite);
      const rimanenti = ordinate.slice(limite);

      const totaleAltre = rimanenti.reduce(
        (sum, item) => sum + item.valore,
        0
      );

      principali.push({
        id_macro: 0,
        nome: `Altre (${rimanenti.length} categorie)`,
        valore: totaleAltre,
        percentuale: Number(
          (
            totaleAltre /
            this.totale *
            100
          ).toFixed(2)
        )
      });

      return principali;
    }
  },

  mounted() {

    this.caricaMacro();

    this.$nextTick(() => {

      this.checkLayoutOverflow();

      if (window.ResizeObserver) {

        this.resizeObserver = new ResizeObserver(() => {

          clearTimeout(this.resizeTimeout);

          this.resizeTimeout = setTimeout(() => {

            this.checkLayoutOverflow();

            if (this.chart) {
              this.chart.resize();
              this.chart.update();
            }

          }, 120);

        });

        this.resizeObserver.observe(
          this.$refs.layoutRoot
        );
      }
    });
  },

  beforeDestroy() {

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    clearTimeout(this.resizeTimeout);

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },

  methods: {

    checkLayoutOverflow() {

      const width = this.$refs.layoutRoot.clientWidth;

      this.$emit("layout-mode", {
        overflow: width < 980
      });
    },

    debouncedCheck() {

      clearTimeout(this.resizeTimeout);

      this.resizeTimeout = setTimeout(() => {

        this.checkLayoutOverflow();

        if (this.chart) {
          this.chart.resize();
        }

      }, 120);
    },

    async caricaMacro() {

      this.loadingMacrocategorie = true;

      try {

        const response = await picService.getMacrocategorie(
          this.anno
        );

        const dati = response.data;

        /*
         * Anni disponibili
         */
        this.anni = [
          {
            value: "all",
            text: "Tutti gli anni"
          },
          ...dati.anni.map(anno => ({
            value: anno,
            text: anno.toString()
          }))
        ];

        /* Totale speso */
        this.totale = dati.categorie.reduce(
          (sum, item) => sum + Number(item.totale),
          0
        );

        /* Macrocategorie */
        /* Prendiamo solo le macrocategorie con importo maggiore di zero per visualizzare solo quelle significative */
        this.categorie = dati.categorie
          .filter(item => Number(item.totale) > 0)
          .map(item => ({
            id_macro: item.id_macro,
            nome: item.nome_macro,
            valore: Number(item.totale),
            percentuale: this.totale
              ? Number(
                (
                  Number(item.totale) /
                  this.totale *
                  100
                ).toFixed(2)
              )
              : 0
          }));

        /* Colori */
        this.assegnaColori();

        /* Grafico */
        this.$nextTick(() => {
          this.creaGrafico();
        });

      } catch (error) {

        console.error(
          "Errore nel caricamento delle macrocategorie:",
          error
        );

      } finally {

        this.loadingMacrocategorie = false;
      }
    },

    assegnaColori() {

      this.categorie.forEach(categoria => {

        if (!this.mappaColori[categoria.id_macro]) {

          this.mappaColori[categoria.id_macro] =
            this.coloriDisponibili[
              Object.keys(this.mappaColori).length %
              this.coloriDisponibili.length
            ];
        }
      });
    },

    coloreCategoria(idMacro) {

      /*
       * La voce "Altre" utilizza l'ultima tonalità
       * disponibile della palette.
       */
      if (idMacro === 0) {
        return this.coloriDisponibili[
          this.coloriDisponibili.length - 1
        ];
      }

      return this.mappaColori[idMacro] || "#999999";
    },

    creaGrafico() {

      if (!this.$refs.pieChart) {
        return;
      }

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      this.chart = new Chart(
        this.$refs.pieChart,
        {
          type: "doughnut",

          data: {

            labels: this.categorieGrafico.map(x => x.nome),

            datasets: [
              {
                data: this.categorieGrafico.map(x => x.valore),

                backgroundColor:
                  this.categorieGrafico.map(
                    x => this.coloreCategoria(x.id_macro)
                  ),

                /*
                 * Chart.js 2.9.4 non dispone della proprietà spacing.
                 * Il bordo bianco viene utilizzato per creare
                 * visivamente la separazione tra le fette.
                 */
                borderColor: "#ffffff",
                borderWidth: 4
              }
            ]
          },

          options: {

            responsive: true,
            maintainAspectRatio: false,

            animation: {
              duration: 300
            },

            layout: {
              padding: {
                top: 8,
                right: 8,
                bottom: 8,
                left: 8
              }
            },

            legend: {
              display: true,
              position: "bottom",

              labels: {
                boxWidth: 13,
                padding: 18,
                fontSize: 13,
                fontStyle: "bold"
              }
            },

            tooltips: {

              titleFontSize: 14,
              titleFontStyle: "bold",
              bodyFontSize: 14,
              bodyFontStyle: "bold",
              xPadding: 12,
              yPadding: 10,

              callbacks: {

                label: (tooltipItem, data) => {

                  const dataset =
                    data.datasets[tooltipItem.datasetIndex];

                  const valore =
                    dataset.data[tooltipItem.index];

                  return (
                    data.labels[tooltipItem.index] +
                    ": " +
                    this.formatEuro(valore)
                  );
                }
              }
            }
          }
        }
      );
    },

    // Formatta l'importo in euro con separatore delle migliaia e due decimali
    formatEuro(value) {

      const numero = new Intl.NumberFormat(
        "it-IT",
        {
          useGrouping: true,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(value || 0);

      return `€ ${numero}`;
    }
  }
};
</script>

<style scoped>

/* ---------------- CONTENITORE ---------------- */

.macro-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
}

/* ---------------- LAYOUT ---------------- */

.macro-layout {
  flex: 1;
  display: flex;
  gap: 6px;
  min-width: 980px;
  overflow: auto;
}

/* ---------------- COLONNA LISTA ---------------- */

.macro-list-col {
  flex: 0 0 64%;
  min-width: 500px;
  display: flex;
  border: none;
}

.macro-list-col > .card {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: none;
}

/* ---------------- COLONNA GRAFICO ---------------- */

.macro-chart-col {
  flex: 1 1 36%;
  min-width: 440px;
  display: flex;
  border: none;
}

.macro-chart-col > .card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: none;
}

/* ---------------- HEADER ---------------- */

/*
 * Gli header interni utilizzano uno sfondo chiaro
 * con testo e bordo nel blu navy della Dashboard.
 */
.panel-header {
  background: #f8f9fb;
  color: #373F66;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-size: 22px;
  font-weight: 600;
  border: 2px solid #dee2e6;
  border-radius: 8px 8px 0 0;
}
/* Centra il titolo dell'header del grafico */
.chart-header {
  justify-content: center;
  text-align: center;
}

.macro-header-title {
  white-space: nowrap;
}

.macro-year {
  width: 150px;
  font-size: 14px;
  margin-left: 15px;

  color: #373F66;
  border-color: #373F66;
  background-color: #fff;
}

.macro-total {
  margin-left: auto;
  white-space: nowrap;
  font-size: 16px;
  color: #373F66;
}

/* ---------------- LISTA ---------------- */

.macro-list-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  background: #f4f6f9;
}
/* ---------------- CARD CATEGORIA ---------------- */

.macro-item {
  background: white;
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
}

.macro-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.macro-name {
  font-weight: 600;
}

.macro-value {
  color: #555;
  white-space: nowrap;
  font-weight: 600;
}

.macro-value span {
  margin-left: 15px;
  font-weight: bold;
  white-space: nowrap;
}

/* ---------------- BARRE ---------------- */

.macro-progress-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.macro-progress {
  flex: 1;
  height: 10px;
  background: #ececec;
  border-radius: 10px;
  overflow: hidden;
}

.macro-progress-bar {
  height: 100%;
  transition: width .3s ease;
}

.macro-percentuale {
  width: 60px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}

/* ---------------- GRAFICO ---------------- */

.macro-chart-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 8px;
  overflow: auto;
  background: #f4f6f9;
}

.macro-chart-body canvas {
  width: 100% !important;
  max-width: 450px;
  max-height: 450px;
  aspect-ratio: 1 / 1;
}

/* ---------------- RESPONSIVE ---------------- */

@media (max-width: 980px) {

  .macro-layout {
    min-width: 980px;
  }
}

/* ---------------- LOADING ---------------- */

.loading-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.35);
}

.loading-box {
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 14px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);

  font-size: 14px;
  font-weight: 500;
}

</style>